import { defineMessages } from 'react-intl';

import { DiagnocoinLedgerEntryType } from 'protocol/api/billing_new/dto_diagnocoin_new_pb';
import {
  EffectDiagnocoinAdjustmentReasonCode,
  EffectProductStateRemoveReasonCode,
  SubscriptionReplacementCause,
} from 'protocol/api/billing_new/dto_order_new_pb';
import {
  ProductStateStatus,
  SubscriptionActivationLimitTrigger,
} from 'protocol/api/billing_new/dto_product_new_pb';

import { SubscriptionActivationLimitTriggerOption } from './types';

export const removeProductReasonMessages = defineMessages<
  Exclude<
    EffectProductStateRemoveReasonCode,
    EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_InvalidValue
  >
>({
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_LimitExhaustedWithReplacement]:
    {
      id: 'removeSubscriptionModal.reasonOption.limitExhaustedWithReplacement',
      defaultMessage: 'Limit exhausted with replacement',
    },
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_DiagnocatEmployeeError]:
    {
      id: 'removeSubscriptionModal.reasonOption.diagnocatEmployeeError',
      defaultMessage: 'Diagnocat employee error',
    },
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ReactivationByAgreement]:
    {
      id: 'removeSubscriptionModal.reasonOption.reactivationByAgreement',
      defaultMessage: 'Reactivation by agreement',
    },
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ContractTermination]:
    {
      id: 'removeSubscriptionModal.reasonOption.contractTermination',
      defaultMessage: 'Contract termination',
    },
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_ConfigurationTesting]:
    {
      id: 'removeSubscriptionModal.reasonOption.configurationTesting',
      defaultMessage: 'Configuration/Testing',
    },
  // Billing sets this one itself when a limit trigger fires. It needs a label so
  // billing history can render it, but it is deliberately absent from the
  // reason picker in RemoveProductModal — an admin cannot choose it.
  [EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_AutoReplacedOnLimitExhaustion]:
    {
      id: 'removeSubscriptionModal.reasonOption.autoReplacedOnLimitExhaustion',
      defaultMessage: 'Limits exhausted — replaced automatically',
    },
});

export const diagnocoinAdjustmentReasonMessages = defineMessages<
  Exclude<
    EffectDiagnocoinAdjustmentReasonCode,
    EffectDiagnocoinAdjustmentReasonCode.EffectDiagnocoinAdjustmentReasonCode_InvalidValue
  >
>({
  [EffectDiagnocoinAdjustmentReasonCode.EffectDiagnocoinAdjustmentReasonCode_DiagnocatEmployeeError]:
    {
      id: 'diagnocoinAdjustment.reasonOption.diagnocatEmployeeError',
      defaultMessage: 'Diagnocat employee error',
    },
  [EffectDiagnocoinAdjustmentReasonCode.EffectDiagnocoinAdjustmentReasonCode_ReactivationByAgreement]:
    {
      id: 'diagnocoinAdjustment.reasonOption.reactivationByAgreement',
      defaultMessage: 'Reactivation by agreement',
    },
  [EffectDiagnocoinAdjustmentReasonCode.EffectDiagnocoinAdjustmentReasonCode_ContractTermination]:
    {
      id: 'diagnocoinAdjustment.reasonOption.contractTermination',
      defaultMessage: 'Contract termination',
    },
  [EffectDiagnocoinAdjustmentReasonCode.EffectDiagnocoinAdjustmentReasonCode_ConfigurationTesting]:
    {
      id: 'diagnocoinAdjustment.reasonOption.configurationTesting',
      defaultMessage: 'Configuration/Testing',
    },
});

// Why a scheduled replacement went live. Billing writes one of these on every
// automatic activation; only LimitsExhausted also closes the subscription it
// replaced, which is why the other two never appear as a removal reason.
export const subscriptionReplacementCauseMessages = defineMessages<
  Exclude<
    SubscriptionReplacementCause,
    SubscriptionReplacementCause.SubscriptionReplacementCause_InvalidValue
  >
>({
  [SubscriptionReplacementCause.SubscriptionReplacementCause_LimitsExhausted]: {
    id: 'subscriptionReplacementCause.limitsExhausted',
    defaultMessage: 'Limits exhausted — replaced automatically',
  },
  [SubscriptionReplacementCause.SubscriptionReplacementCause_SourceLifetimeEnded]:
    {
      id: 'subscriptionReplacementCause.sourceLifetimeEnded',
      defaultMessage: 'Previous subscription ended',
    },
  [SubscriptionReplacementCause.SubscriptionReplacementCause_SourceRemoved]: {
    id: 'subscriptionReplacementCause.sourceRemoved',
    defaultMessage: 'Previous subscription was removed',
  },
});

export const orderReasonMessages = defineMessages({
  reasonLabel: {
    id: 'billing.orderReason.label',
    defaultMessage: 'Reason',
  },
  reasonRequired: {
    id: 'billing.orderReason.required',
    defaultMessage: 'Please select a reason',
  },
  commentLabel: {
    id: 'billing.orderReason.commentLabel',
    defaultMessage: 'Comment',
  },
  commentPlaceholder: {
    id: 'billing.orderReason.commentPlaceholder',
    defaultMessage: 'HappyDesk ticket, contract link, agreement details, etc.',
  },
});

export const removeSubscriptionModalMessages = defineMessages({
  title: {
    id: 'removeSubscriptionModal.removeSubscriptionModalTitle',
    defaultMessage: 'ATTENTION!',
  },
  okText: {
    id: 'removeSubscriptionModal.removeSubscriptionModalOkText',
    defaultMessage: 'Yes, remove subscription',
  },
  cancelText: {
    id: 'removeSubscriptionModal.removeSubscriptionModalCancelText',
    defaultMessage: "No, I didn't mean it!",
  },
  content: {
    id: 'removeSubscriptionModal.removeSubscriptionModalContent',
    defaultMessage:
      'You are about to remove user subscription. It will disrupt user ability to use the application because they will lose their subscription allowance. Please make sure they are aware of their subscription removal.',
  },
  confirmation: {
    id: 'removeSubscriptionModal.removeSubscriptionModalContent2',
    defaultMessage: 'Are you sure you want to remove the subscription?',
  },
  success: {
    id: 'removeSubscriptionModal.success',
    defaultMessage: 'Subscription successfully removed',
  },
  error: {
    id: 'removeSubscriptionModal.error',
    defaultMessage: 'Something went wrong',
  },
  noPermission: {
    id: 'removeSubscriptionModal.noPermission',
    defaultMessage: 'You do not have permission to remove this subscription.',
  },
});

export const removePackageModalMessages = defineMessages({
  title: {
    id: 'removePackageModal.removePackageModalTitle',
    defaultMessage: 'ATTENTION!',
  },
  okText: {
    id: 'removePackageModal.removePackageModalOkText',
    defaultMessage: 'Yes, remove package',
  },
  cancelText: {
    id: 'removePackageModal.removePackageModalCancelText',
    defaultMessage: "No, I didn't mean it!",
  },
  content: {
    id: 'removePackageModal.removePackageModalContent',
    defaultMessage:
      'You are about to remove user package. It will disrupt user ability to use the application because they will lose their package allowance. Please make sure they are aware of their package removal.',
  },
  confirmation: {
    id: 'removePackageModal.removePackageModalContent2',
    defaultMessage: 'Are you sure you want to remove the package?',
  },
  success: {
    id: 'removePackageModal.success',
    defaultMessage: 'Package successfully removed',
  },
  noPermission: {
    id: 'removePackageModal.noPermission',
    defaultMessage: 'You do not have permission to remove this package.',
  },
});

export const diagnocoinLedgerEntryTypeMessages =
  defineMessages<DiagnocoinLedgerEntryType>({
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeInvalidValue]: {
      id: 'diagnocoins.history.type.unknown',
      defaultMessage: 'Balance change',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeCredit]: {
      id: 'diagnocoins.history.type.credit',
      defaultMessage: 'Coins purchased',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeReserve]: {
      id: 'diagnocoins.history.type.reserve',
      defaultMessage: 'AI report ordered',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeCommit]: {
      id: 'diagnocoins.history.type.commit',
      defaultMessage: 'AI report completed',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeRelease]: {
      id: 'diagnocoins.history.type.release',
      defaultMessage: 'AI report failed — coins returned',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeReleaseExpired]: {
      id: 'diagnocoins.history.type.releaseExpired',
      defaultMessage: 'AI report failed after the coins had expired',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeExpire]: {
      id: 'diagnocoins.history.type.expire',
      defaultMessage: 'Coins expired',
    },
    [DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeAdjustment]: {
      id: 'diagnocoins.history.type.adjustment',
      defaultMessage: 'Balance correction',
    },
  });

export const subscriptionStatusMessages = defineMessages<ProductStateStatus>({
  [ProductStateStatus.ProductStateStatusInvalidValue]: {
    id: 'subscriptionStatus.invalid',
    defaultMessage: 'Invalid',
  },
  [ProductStateStatus.ProductStateStatusActive]: {
    id: 'subscriptionStatus.active',
    defaultMessage: 'Active',
  },
  [ProductStateStatus.ProductStateStatusAwaitingActivation]: {
    id: 'subscriptionStatus.awaitingActivation',
    defaultMessage: 'Awaiting activation',
  },
  [ProductStateStatus.ProductStateStatusDeleted]: {
    id: 'subscriptionStatus.deleted',
    defaultMessage: 'Deleted',
  },
  [ProductStateStatus.ProductStateStatusLifeTimeIsOver]: {
    id: 'subscriptionStatus.expired',
    defaultMessage: 'Expired',
  },
});

export const subscriptionActivationLimitTriggerMessages =
  defineMessages<SubscriptionActivationLimitTriggerOption>({
    [SubscriptionActivationLimitTrigger.SubscriptionActivationLimitTrigger_Analysis2DExhausted]:
      {
        id: 'subscriptionActivationTrigger.analysis2DExhausted',
        defaultMessage: '2D report limit is exhausted',
      },
    [SubscriptionActivationLimitTrigger.SubscriptionActivationLimitTrigger_Analysis3DExhausted]:
      {
        id: 'subscriptionActivationTrigger.analysis3DExhausted',
        defaultMessage: '3D report limit is exhausted',
      },
    [SubscriptionActivationLimitTrigger.SubscriptionActivationLimitTrigger_Analysis2DOr3DExhausted]:
      {
        id: 'subscriptionActivationTrigger.analysis2DOr3DExhausted',
        defaultMessage: '2D or 3D report limit is exhausted',
      },
    [SubscriptionActivationLimitTrigger.SubscriptionActivationLimitTrigger_Analysis2DAnd3DExhausted]:
      {
        id: 'subscriptionActivationTrigger.analysis2DAnd3DExhausted',
        defaultMessage: 'Both 2D and 3D report limits are exhausted',
      },
  });

export const scheduledSubscriptionMessages = defineMessages({
  activationTrigger: {
    id: 'scheduledSubscription.activationTrigger',
    defaultMessage: 'Activation trigger: {trigger}',
  },
  noStartDate: {
    id: 'scheduledSubscription.noStartDate',
    defaultMessage: 'Starts when the trigger fires',
  },
});
